/* RESPONSIBLE TEAM: team-ai-insights */

import {
  type CxBenchmarkIndustry,
  type CxBenchmarkData,
  type CxBenchmarkGroup,
} from 'embercom/components/reporting/bespoke/fin-performance/cx-benchmark-panel/types';
import type IntlService from 'ember-intl/services/intl';
import { CX_BENCHMARK_DATA } from './benchmark-data';

// Returns the full benchmark dataset.
// Currently returns static production data; will be replaced by an API call.
export function getCxBenchmarkData(): CxBenchmarkData {
  return CX_BENCHMARK_DATA;
}

// Looks up a benchmark group by industry and segment using the composite key format.
// Returns undefined if the group doesn't exist (below the 30-app threshold).
export function getBenchmarkGroup(
  data: CxBenchmarkData,
  industry: CxBenchmarkIndustry,
  segment: string,
): CxBenchmarkGroup | undefined {
  let key = `${industry}:${segment}`;
  return data.benchmarks[key];
}

// Returns the human-readable display name for an industry slug.
export function getIndustryDisplayName(intl: IntlService, industry: CxBenchmarkIndustry): string {
  return intl.t(`reporting.cx-benchmarks.industries.${industry}`);
}

// Returns all available industry options for the dropdown selector.
// Only includes industries that have benchmark data in the dataset.
export function getAvailableIndustries(data: CxBenchmarkData): CxBenchmarkIndustry[] {
  let seen: Record<string, boolean> = {};
  let industries: CxBenchmarkIndustry[] = [];
  Object.keys(data.benchmarks).forEach((key) => {
    let industry = key.split(':')[0];
    if (industry && !seen[industry]) {
      seen[industry] = true;
      industries.push(industry as CxBenchmarkIndustry);
    }
  });
  return industries;
}

// Interpolates the app's percentile rank from the 20-point percentile distribution.
// Returns a value between 0 and 100 representing the percentile rank.
export function interpolatePercentileRank(group: CxBenchmarkGroup, appCxScore: number): number {
  let keys = Object.keys(group.percentiles);
  let sortedEntries = keys
    .map((key) => ({
      percentile: parseInt(key.replace('p', ''), 10),
      value: group.percentiles[key as keyof typeof group.percentiles],
    }))
    .sort((a, b) => a.percentile - b.percentile);

  // Below the lowest percentile
  if (appCxScore <= sortedEntries[0].value) {
    return sortedEntries[0].percentile;
  }

  // Above the highest percentile
  if (appCxScore >= sortedEntries[sortedEntries.length - 1].value) {
    return sortedEntries[sortedEntries.length - 1].percentile;
  }

  // Linear interpolation between the two nearest percentile markers
  for (let i = 0; i < sortedEntries.length - 1; i++) {
    let lower = sortedEntries[i];
    let upper = sortedEntries[i + 1];
    if (appCxScore >= lower.value && appCxScore <= upper.value) {
      let ratio = (appCxScore - lower.value) / (upper.value - lower.value);
      return lower.percentile + ratio * (upper.percentile - lower.percentile);
    }
  }

  return 50; // fallback
}
